/** Row component for the cross-session audit dashboard. Renders one
 * session's lightweight summary + a click-through link to the per-
 * session detail page (the heavyweight chain walk lives there). */
import { Link } from "react-router-dom";
import { formatDate, formatTime } from "@/lib/format";

import type { AuditSessionRow } from "./types";


type Props = { row: AuditSessionRow };


export function SessionSummaryRow({ row }: Props) {
  const tokenBadge = tokenBadgeFor(row);
  return (
    <li className="grid grid-cols-12 items-baseline gap-3 px-4 py-3 hover:bg-nb-surface/40">
      <Link
        to={`/sessions/${row.session_id}/audit`}
        className="col-span-5 truncate font-mono text-xs text-nb-mint hover:underline"
        title={row.session_id}
      >
        {row.session_id.slice(0, 8)}…
      </Link>
      <span className="col-span-2 pill text-xs">{row.status}</span>
      <span className="col-span-2 text-xs text-nb-text-2">
        {formatDate(row.created_at)}{" "}
        <span className="text-nb-text-3">
          {formatTime(row.created_at)}
        </span>
      </span>
      <span className="col-span-1 text-right text-xs text-nb-text-2">
        {row.event_count}
      </span>
      <span className={`col-span-2 text-right text-xs ${tokenBadge.className}`}>
        {tokenBadge.label}
      </span>
    </li>
  );
}


function tokenBadgeFor(row: AuditSessionRow): { label: string; className: string } {
  const { anchored, pending, failed } = row.tokens;
  if (failed > 0) {
    return { label: `${failed} failed`, className: "text-cvp-danger" };
  }
  if (anchored > 0 && pending === 0) {
    return { label: `✓ ${anchored} anchored`, className: "text-cvp-ok" };
  }
  if (pending > 0 && anchored > 0) {
    return {
      label: `${anchored}✓ · ${pending}⏳`,
      className: "text-cvp-warn",
    };
  }
  if (pending > 0) {
    return { label: `⏳ ${pending} pending`, className: "text-cvp-warn" };
  }
  return { label: "— no anchors", className: "text-nb-text-3" };
}
